if (!customElements.get('cart-discount')) {
  class CartDiscount extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="discount"]');
      this.button = this.form?.querySelector('button[type="submit"]');
      this.errorWrap = this.querySelector('.cart-discount__error');
      this.errorDiscount = this.querySelector('.cart-discount__error--discount');
      this.errorShipping = this.querySelector('.cart-discount__error--shipping');
      this.onApply = this.applyDiscount.bind(this);
      this.onRemove = this.removeDiscount.bind(this);
    }

    connectedCallback() {
      if (this.form) this.form.addEventListener('submit', this.onApply);
      this.addEventListener('click', this.onRemove);
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onApply);
      this.removeEventListener('click', this.onRemove);
    }

    normalizeCode(code) {
      return code.trim().toLowerCase();
    }

    getCurrentCodes() {
      return Array.from(document.querySelectorAll('[data-discount-code]'))
        .map((el) => el.dataset.discountCode)
        .filter(Boolean);
    }

    getSectionsToRender() {
      const main = document.getElementById('main-cart-items');
      const mainSectionId = main?.dataset.id;
      const sections = [];

      if (mainSectionId) {
        sections.push(
          { id: 'main-cart-items', section: mainSectionId, selector: '.js-contents' },
          { id: 'main-cart-total', section: mainSectionId, selector: '.js-contents-total' },
          { id: 'main-cart-shipping', section: mainSectionId, selector: '.js-contents-shipping' },
          { id: 'main-cart-discount', section: mainSectionId, selector: '.js-contents-discount' },
        );
      }

      sections.push(
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      );

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        sections.push({ id: 'cart-drawer', section: 'cart-drawer', selector: null });
      }

      return sections;
    }

    async applyDiscount(e) {
      e.preventDefault();
      const code = this.input?.value.trim();
      if (!code) return;

      this.toggleError(false);
      this.setLoading(true);

      const existing = this.getCurrentCodes();
      const normalizedExisting = existing.map((existingCode) => this.normalizeCode(existingCode));
      const codes = normalizedExisting.includes(this.normalizeCode(code)) ? existing : [...existing, code];

      await this.updateDiscounts(codes, code);
      if (this.input) this.input.value = '';
    }

    async removeDiscount(e) {
      const pill = e.target.closest('[data-remove-discount]');
      if (!pill) return;

      e.preventDefault();
      this.toggleError(false);
      this.setLoading(true);

      const codeToRemove = pill.dataset.removeDiscount;
      const keep = this.getCurrentCodes().filter((code) => code !== codeToRemove);

      await this.updateDiscounts(keep, null);
    }

    async updateDiscounts(codes, lastCode = codes[codes.length - 1]) {
      try {
        const sectionsMeta = this.getSectionsToRender();
        const sections = sectionsMeta.map((section) => section.section);

        const body = JSON.stringify({
          discount: codes.length ? codes.join(',') : '',
          sections,
          sections_url: window.location.pathname,
        });

        const res = await fetch(`${window.routes.cart_update_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body,
        });

        if (!res.ok) throw new Error('Failed to update discounts');

        const data = await res.json();
        const appliedEntries = Array.isArray(data.discount_codes) ? data.discount_codes : [];
        const appliedCodes = appliedEntries.map((entry) => entry.code);

        if (lastCode) {
          const matchedEntry = appliedEntries.find(
            (entry) => this.normalizeCode(entry.code) === this.normalizeCode(lastCode)
          );

          if (!matchedEntry || matchedEntry.applicable === false) {
            this.toggleError(true, 'discount_code');
            return;
          }

          if (this.isShippingOnlyDiscount(data, matchedEntry)) {
            this.toggleError(true, 'shipping');
          } else {
            this.toggleError(false);
          }
        } else {
          this.toggleError(false);
        }

        this.renderUpdatedCart(data, sectionsMeta);

        document.getElementById('discount-code-modal')?.close?.();

        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('cartUpdated'));
        }, 100);
      } catch (err) {
        console.error('[CartDiscount] updateDiscounts error:', err);
        this.toggleError(true, 'discount_code');
      } finally {
        this.setLoading(false);
      }
    }

    isShippingOnlyDiscount(cartData, discountEntry) {
      if (!discountEntry?.applicable) return false;

      const hasCartLevelDiscount = Array.isArray(cartData.cart_level_discount_applications)
        && cartData.cart_level_discount_applications.length > 0;
      const hasLineLevelDiscount = Array.isArray(cartData.items)
        && cartData.items.some((item) => Array.isArray(item.line_level_discount_allocations)
          && item.line_level_discount_allocations.length > 0);

      return !hasCartLevelDiscount && !hasLineLevelDiscount;
    }

    renderUpdatedCart(data, sectionsMeta) {
      const cartDrawer = document.querySelector('cart-drawer');
      const sectionHtml = data.sections?.['cart-drawer'];

      if (cartDrawer && sectionHtml && typeof cartDrawer.refreshPartial === 'function') {
        cartDrawer.refreshPartial(sectionHtml);
      } else if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data);
      }

      sectionsMeta.forEach(({ id, section, selector }) => {
        if (section === 'cart-drawer') return;

        const container = document.getElementById(id);
        const htmlStr = data.sections?.[section];
        if (!container || !htmlStr) return;

        const doc = new DOMParser().parseFromString(htmlStr, 'text/html');
        const fragment = selector ? doc.querySelector(selector) : doc.body;
        if (!fragment) return;

        const target = selector ? (container.querySelector(selector) || container) : container;
        target.innerHTML = fragment.innerHTML;
      });
    }

    toggleError(show, type = 'discount_code') {
      if (!this.errorWrap) return;

      if (!show) {
        this.errorWrap.classList.add('hidden');
        this.errorDiscount?.classList.add('hidden');
        this.errorShipping?.classList.add('hidden');
        return;
      }

      this.errorWrap.classList.remove('hidden');
      this.errorDiscount?.classList.toggle('hidden', type !== 'discount_code');
      this.errorShipping?.classList.toggle('hidden', type !== 'shipping');
    }

    setLoading(isLoading) {
      if (!this.button) return;
      this.button.disabled = isLoading;
      this.button.classList.toggle('loading', isLoading);
    }
  }

  customElements.define('cart-discount', CartDiscount);
}
